import { type FC, useState, useMemo } from 'react';
import {
  ForgeButton,
  ForgeCheckbox,
  ForgeIcon,
  ForgeIconButton,
  ForgeLabelValue,
  ForgePopover,
  ForgeSelect,
  ForgeSlider,
} from '@tylertech/forge-react';

import './index.scss';
import { type FilterMetadataCollection } from 'common/types/filterMetadata';
import {
  PDF_CONFIGURATION_COLUMN_WIDTH,
  PDF_CONFIGURATION_GROUP_EXPANSION,
  PDF_CONFIGURATION_METADATA_VISIBILITY,
  PDF_CONFIGURATION_ORIENTATION,
  PDF_CONFIGURATION_PAGE_BREAK,
  PDF_CONFIGURATION_PAGE_SIZE,
  PDF_CONFIGURATION_TEXT_OVERFLOW,
} from '../../constants';
import { type AttachmentConfiguration, type PdfFormat } from '../../types/attachment';
import OutsideClickDetector from './OutsideClickDetector';
import { PRINT_PREVIEW_FONT_SLIDER_CONFIG } from 'common/components/PdfPreviewModal/templates/section/defaultStyles';
import { type GroupExpansion, type MetadataVisibility } from 'common/components/PdfPreviewModal/types';
import { type ScheduleSendValues } from '../../types/scheduleSend';
import { I18n } from 'common/i18n';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import {
  isTablePrintingImprovementForScheduledSendEnabled,
  isTablePrintingImprovementGroupExpansionEnabled,
  isTablePrintingImprovementHideGridlinesEnabled,
  isTablePrintingImprovementHideElementsEnabled,
  isTablePrintingImprovementTextSizeSliderEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { SoQLType } from 'common/types/soql';

const scope = 'shared.components.schedule_send_report.modal.attachment_section.pdf_configuration';

const PDF_DEFAULTS = {
  PAGE_SIZE: 'Letter',
  ORIENTATION: 'landscape',
  TEXT_OVERFLOW: 'linebreak',
  PAGE_BREAK: 'standard',
  GROUP_EXPANSION: 'expanded',
  METADATA_VISIBILITY: 'all',
} as const;

const COLUMN_WIDTH_INHERIT = 'inherit';
const FORMAT_PDF = 'pdf';
const TABLE_FULL_REPORT = 'full-report';

const getPdfOptionsWithDefaults = (pdfConfigValues: PdfFormat | undefined) => ({
  pageSize: pdfConfigValues?.pageSize ?? PDF_DEFAULTS.PAGE_SIZE,
  orientation: pdfConfigValues?.orientation ?? PDF_DEFAULTS.ORIENTATION,
  columnWidth:
    pdfConfigValues?.columnWidth ??
    (isTablePrintingImprovementTextSizeSliderEnabled() ? 'inherit' : 'bestfit'),
  textOverflow: pdfConfigValues?.textOverflow ?? PDF_DEFAULTS.TEXT_OVERFLOW,
  pageBreak: pdfConfigValues?.pageBreak ?? PDF_DEFAULTS.PAGE_BREAK,
  fontSize: pdfConfigValues?.fontSize ?? PRINT_PREVIEW_FONT_SLIDER_CONFIG.DEFAULT,
  groupExpansion: pdfConfigValues?.groupExpansion ?? PDF_DEFAULTS.GROUP_EXPANSION,
  ...(isTablePrintingImprovementForScheduledSendEnabled() && {
    reportingPeriod: pdfConfigValues?.reportingPeriod ?? undefined,
  }),
  ...(isTablePrintingImprovementHideGridlinesEnabled() &&
    isTablePrintingImprovementForScheduledSendEnabled() && {
      showGridlines: pdfConfigValues?.showGridlines ?? true,
    }),
  ...(isTablePrintingImprovementHideElementsEnabled() &&
    isTablePrintingImprovementForScheduledSendEnabled() && {
      showMetadata: pdfConfigValues?.showMetadata ?? PDF_DEFAULTS.METADATA_VISIBILITY,
    }),
});

export interface PdfConfigurationProps {
  index: number;
  tableCurrentValue: string;
  formatCurrentValue: string;
  attachmentConfigs: AttachmentConfiguration[];
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
  isTableWithGrouping: boolean;
  filterMetadata?: FilterMetadataCollection;
}

export const PdfConfiguration: FC<PdfConfigurationProps> = (props) => {
  const {
    index,
    tableCurrentValue,
    formatCurrentValue,
    attachmentConfigs,
    updateScheduleSendValue,
    isTableWithGrouping,
    filterMetadata,
  } = props;

  const pdfConfigValues = attachmentConfigs[index].pdfOptions;
  const pdfOptionsSavedValues = useMemo(() => getPdfOptionsWithDefaults(pdfConfigValues), [pdfConfigValues]);
  const [pageSize, setPageSize] = useState<string>(pdfOptionsSavedValues.pageSize);
  const [orientation, setOrientation] = useState<string>(pdfOptionsSavedValues.orientation);
  const [columnWidth, setColumnWidth] = useState<string>(pdfOptionsSavedValues.columnWidth);
  const [textOverflow, setTextOverflow] = useState<string>(pdfOptionsSavedValues.textOverflow);
  const [pageBreak, setPageBreak] = useState<string>(pdfOptionsSavedValues.pageBreak);
  const [fontSize, setFontSize] = useState<number>(pdfOptionsSavedValues.fontSize);
  const [groupExpansion, setGroupExpansion] = useState<GroupExpansion>(pdfOptionsSavedValues.groupExpansion);
  const [reportingPeriod, setReportingPeriod] = useState<string | undefined>(
    pdfOptionsSavedValues.reportingPeriod,
  );
  const [showGridlines, setShowGridlines] = useState<boolean>(pdfOptionsSavedValues.showGridlines ?? true);
  const [showMetadata, setShowMetadata] = useState<MetadataVisibility>(
    pdfOptionsSavedValues.showMetadata ?? 'all',
  );
  const [flyoutOpen, setFlyoutOpen] = useState<boolean>(false);

  const showGroupExpansion =
    isTablePrintingImprovementGroupExpansionEnabled() &&
    isTablePrintingImprovementForScheduledSendEnabled() &&
    isTableWithGrouping;

  const showGridlinesOption =
    isTablePrintingImprovementHideGridlinesEnabled() && isTablePrintingImprovementForScheduledSendEnabled();

  const showMetadataVisibility =
    isTablePrintingImprovementHideElementsEnabled() && isTablePrintingImprovementForScheduledSendEnabled();

  // Extract date filters from filterMetadata for the current table
  const dateFilters = useMemo(() => {
    if (
      tableCurrentValue === 'full-report' ||
      !filterMetadata ||
      !isTablePrintingImprovementForScheduledSendEnabled()
    ) {
      return [];
    }

    // Get filter metadata for the current table
    const tableFilterMetadata = filterMetadata[tableCurrentValue] || [];

    // Filter to only date filters
    const tableDateFilters = tableFilterMetadata.filter(
      (filter) => filter.dataTypeName === SoQLType.SoQLFixedTimestampT,
    );

    return tableDateFilters;
  }, [tableCurrentValue, filterMetadata]);

  const handleSelectChange = (setter: (value: string) => void) => (event: CustomEvent) => {
    const value = event.detail as string;
    setter(value);
  };

  const onPageSizeChange = handleSelectChange(setPageSize);
  const onOrientationChange = handleSelectChange(setOrientation);
  const onColumnWidthChange = handleSelectChange(setColumnWidth);
  const onTextOverflowChange = handleSelectChange(setTextOverflow);
  const onPageBreakChange = handleSelectChange(setPageBreak);

  const onGroupExpansionChange = (event: CustomEvent) => {
    setGroupExpansion(event.detail as GroupExpansion);
  };

  const onReportingPeriodChange = (event: CustomEvent) => {
    const value = event.detail as string;
    setReportingPeriod(value || undefined);
  };

  const onMetadataVisibilityChange = (event: CustomEvent) => {
    setShowMetadata(event.detail as MetadataVisibility);
  };

  const onFontSizeChange = (event: CustomEvent) => {
    const sliderValue = event.detail.value as number;
    setFontSize(sliderValue);
  };

  const closePopover = () => {
    setFlyoutOpen(false);
  };

  function onSavePdfConfig() {
    const pdfOptions = {
      pageSize,
      orientation,
      columnWidth,
      textOverflow,
      ...(isTableWithGrouping && { pageBreak }),
      ...(columnWidth === COLUMN_WIDTH_INHERIT && { fontSize }),
      ...(showGroupExpansion && { groupExpansion }),
      ...(isTablePrintingImprovementForScheduledSendEnabled() && reportingPeriod && { reportingPeriod }),
      ...(showGridlinesOption && { showGridlines }),
      ...(showMetadataVisibility && { showMetadata }),
    };

    attachmentConfigs[index].pdfOptions = pdfOptions;
    updateScheduleSendValue('attachmentConfigs', attachmentConfigs);
    closePopover();
  }

  const resetStates = () => {
    setPageSize(pdfOptionsSavedValues.pageSize);
    setOrientation(pdfOptionsSavedValues.orientation);
    setColumnWidth(pdfOptionsSavedValues.columnWidth);
    setTextOverflow(pdfOptionsSavedValues.textOverflow);
    setPageBreak(pdfOptionsSavedValues.pageBreak);
    setFontSize(pdfOptionsSavedValues.fontSize);
    setGroupExpansion(pdfOptionsSavedValues.groupExpansion);
    if (isTablePrintingImprovementForScheduledSendEnabled()) {
      setReportingPeriod(pdfOptionsSavedValues.reportingPeriod);
    }
    if (showGridlinesOption) {
      setShowGridlines(pdfOptionsSavedValues.showGridlines ?? true);
    }
    if (showMetadataVisibility) {
      setShowMetadata(pdfOptionsSavedValues.showMetadata ?? 'all');
    }
  };

  const handleCancel = () => {
    resetStates();
    closePopover();
  };

  useEscapeKey({
    onKeydown: handleCancel,
    enabled: flyoutOpen,
    eventTarget: document,
  });

  return (
    <OutsideClickDetector onOutsideClick={handleCancel}>
      <div>
        <ForgeIconButton
          densityLevel={3}
          slot="trailing"
          className="pdf-options-btn"
          data-testid="pdf-settings-btn"
          id={`pdf-options-${index}`}
          hidden={formatCurrentValue !== FORMAT_PDF || tableCurrentValue === TABLE_FULL_REPORT}
          dense
          onClick={() => {
            setFlyoutOpen(true);
          }}
        >
          <ForgeIcon slot="end" name="settings" />
        </ForgeIconButton>

        {flyoutOpen && (
          <ForgePopover
            data-testid="pdf-config-popover"
            anchor={`pdf-options-${index}`}
            placement={'right'}
            open
          >
            <div className="pdf-popover pdf-popover--scrollable">
              <div className="forge-typography--body2 pdf-configurations-header">
                {I18n.t('title', { scope })}
              </div>
              <ForgeSelect
                className="pdf-config-select"
                data-testid="page-size-select"
                label={I18n.t('page_size', { scope })}
                labelPosition="block-start"
                value={pageSize}
                options={PDF_CONFIGURATION_PAGE_SIZE}
                syncPopupWidth
                on-change={(event: CustomEvent) => onPageSizeChange(event)}
              />

              <ForgeSelect
                className="pdf-config-select"
                data-testid="orientation-select"
                label={I18n.t('orientation', { scope })}
                labelPosition="block-start"
                value={orientation}
                options={PDF_CONFIGURATION_ORIENTATION}
                syncPopupWidth
                on-change={(event: CustomEvent) => onOrientationChange(event)}
              />

              <ForgeSelect
                className="pdf-config-select"
                data-testid="column-width-select"
                label={I18n.t('column_width', { scope })}
                labelPosition="block-start"
                value={columnWidth}
                options={
                  isTablePrintingImprovementTextSizeSliderEnabled()
                    ? PDF_CONFIGURATION_COLUMN_WIDTH.reverse()
                    : PDF_CONFIGURATION_COLUMN_WIDTH
                }
                syncPopupWidth
                on-change={(event: CustomEvent) => onColumnWidthChange(event)}
              />

              {columnWidth === COLUMN_WIDTH_INHERIT && (
                <div data-testid="font-size-slider-container">
                  <ForgeLabelValue id="font-size-slider" className="label">
                    <span slot="label" id="font-size-slider-label">
                      {I18n.t('font_size', { scope })}
                    </span>
                  </ForgeLabelValue>
                  <ForgeSlider
                    role="forge-slider"
                    style={{ width: '100%' }}
                    type="continuous"
                    min={PRINT_PREVIEW_FONT_SLIDER_CONFIG.MIN}
                    max={PRINT_PREVIEW_FONT_SLIDER_CONFIG.MAX}
                    value={fontSize}
                    step={PRINT_PREVIEW_FONT_SLIDER_CONFIG.STEP}
                    on-forge-slider-change={(event: CustomEvent) => onFontSizeChange(event)}
                  />
                </div>
              )}

              <ForgeSelect
                className="pdf-config-select"
                data-testid="text-overflow-select"
                label={I18n.t('text_overflow', { scope })}
                labelPosition="block-start"
                value={textOverflow}
                options={PDF_CONFIGURATION_TEXT_OVERFLOW}
                syncPopupWidth
                on-change={(event: CustomEvent) => onTextOverflowChange(event)}
              />

              {isTableWithGrouping && (
                <ForgeSelect
                  className="pdf-config-select"
                  data-testid="page-break-select"
                  label={I18n.t('page_break', { scope })}
                  labelPosition="block-start"
                  value={pageBreak}
                  options={PDF_CONFIGURATION_PAGE_BREAK}
                  syncPopupWidth
                  on-change={(event: CustomEvent) => onPageBreakChange(event)}
                />
              )}

              {showGroupExpansion && (
                <ForgeSelect
                  className="pdf-config-select"
                  data-testid="group-expansion-select"
                  label={I18n.t('group_expansion', { scope })}
                  labelPosition="block-start"
                  value={groupExpansion}
                  options={PDF_CONFIGURATION_GROUP_EXPANSION}
                  syncPopupWidth
                  on-change={(event: CustomEvent) => onGroupExpansionChange(event)}
                />
              )}

              {isTablePrintingImprovementForScheduledSendEnabled() && dateFilters.length > 0 && (
                <ForgeSelect
                  className="pdf-config-select"
                  data-testid="reporting-period-select"
                  label={I18n.t('reporting_period.label', { scope })}
                  labelPosition="block-start"
                  value={reportingPeriod || ''}
                  options={[
                    { label: I18n.t('reporting_period.no_filter_selected', { scope }), value: '' },
                    ...dateFilters.map((filter) => ({
                      label: filter.displayName || '',
                      value: filter.fieldName || '',
                    })),
                  ]}
                  syncPopupWidth
                  on-change={(event: CustomEvent) => onReportingPeriodChange(event)}
                />
              )}

              {showMetadataVisibility && (
                <ForgeSelect
                  className="pdf-config-select"
                  data-testid="metadata-visibility-select"
                  label={I18n.t('metadata_visibility', { scope })}
                  labelPosition="block-start"
                  value={showMetadata}
                  options={PDF_CONFIGURATION_METADATA_VISIBILITY}
                  syncPopupWidth
                  on-change={(event: CustomEvent) => onMetadataVisibilityChange(event)}
                />
              )}

              {showGridlinesOption && (
                <ForgeCheckbox
                  data-testid="show-gridlines-checkbox"
                  className="pdf-config-checkbox"
                  checked={showGridlines}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setShowGridlines(event.target.checked)
                  }
                >
                  <span>
                    {I18n.t('show_gridlines', { scope: 'shared.components.schedule_send_report.modal' })}
                  </span>
                </ForgeCheckbox>
              )}

              <ForgeButton
                data-testid="save-pdf-config-btn"
                className="save-pdf-config"
                variant="raised"
                onClick={onSavePdfConfig}
              >
                <span>{I18n.t('save', { scope })}</span>
              </ForgeButton>
            </div>
          </ForgePopover>
        )}
      </div>
    </OutsideClickDetector>
  );
};
